/* RESPONSIBLE TEAM: team-knowledge-foundations */

/**
 * Knowledge Hub Folder andSync Source Helpers
 *
 * This utility module contains functions related to folders and
 * sync source relationships in the Knowledge Hub feature area.
 *
 * These functions are not part of the FilterableList component to:
 * 1. Enable reusability - these folder-related utilities can be used by other components
 *    that need to understand folder hierarchies and sync relationships
 * 2. Improve testability - allowing direct testing without accessing private component methods
 * 3. Separate concerns - keeping the component focused on UI/component logic while
 *    moving data transformation and traversal logic to utilities
 */

import type Folder from 'embercom/models/content-service/folder';
import type SyncSourceWrapper from 'embercom/models/knowledge-hub/sync-source-wrapper';
import type Store from '@ember-data/store';
import { SyncStatus } from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { States } from 'embercom/lib/knowledge-hub/constants';

/**
 * Finds the root synced folder for the given folder by recursively traversing up
 * the folder hierarchy. Returns the folder itself if it's a root synced folder,
 * or the first ancestor that is a root synced folder.
 */
export function findRootSyncedFolder(folder?: Folder): Folder | undefined {
  // Base case 1: No folder to check (we've reached the top of the hierarchy
  // or started with no folder at all). In this case, no root synced folder exists.
  if (!folder) {
    return undefined;
  }

  // Base case 2: Current folder is a root synced folder
  if (isRootSyncedFolder(folder)) {
    return folder;
  }

  // Recursive case: Check the parent folder
  return findRootSyncedFolder(folder.parentFolder);
}

/**
 * Determines if a folder is a root synced folder - meaning it's both
 * synced from an external source and is the root folder of that sync.
 */
export function isRootSyncedFolder(folder: Folder): boolean {
  return folder && folder.isSynced && folder.isRoot;
}

/**
 * Gets the sync source associated with the given folder ID.
 */
export function getSyncSourceForFolder(
  store: Store,
  folderId: string,
): SyncSourceWrapper | undefined {
  let sources = store.peekAll('knowledge-hub/sync-source-wrapper');
  return sources.find((source) => {
    if (!source.folderId) {
      return false;
    }
    return source.folderId.toString() === folderId;
  });
}

/**
 * Translates a sync status string to the corresponding state number.
 * External content for web sources use default value of DRAFT. We can safely assume this == LIVE
 * as it would not exist if not synced successfully.
 */
export function translateSyncStatusToState(syncStatus: string): number {
  switch (syncStatus) {
    case SyncStatus.LIVE:
      return States.DRAFT;
    case SyncStatus.ERROR:
      return States.ERROR;
    case SyncStatus.SYNCING:
      return States.SYNCING;
    case SyncStatus.SYNCED:
      return States.DRAFT;
    case SyncStatus.EXCLUDED:
      return States.SUSPENDED;
    case SyncStatus.FILTERED:
      return States.FILTERED;
    case SyncStatus.PENDING_VERIFICATION:
      return States.PAUSED;
    default:
      return States.ERROR;
  }
}
